import type {
  MobileAuthResultView,
  MobileNotificationDeviceView,
  MobileNotificationPreferences,
  MobileControlState,
} from 'shared';

export interface FileDownloadInput {
  name: string;
  url?: string;
  blob?: Blob;
}

export interface MobileUpdateState {
  revision: number;
  currentVersion: string;
  latestVersion: string | null;
  phase: 'idle' | 'checking' | 'available' | 'downloading' | 'downloaded' | 'error' | 'disabled';
  percent: number;
  releaseNotes: string;
  message: string;
  autoDownload: boolean;
  lastCheckedAt: number;
}

export interface MobileUpdateAdapter {
  status(): Promise<MobileUpdateState>;
  check(): Promise<MobileUpdateState>;
  download(): Promise<MobileUpdateState>;
  install(): Promise<MobileUpdateState>;
  setAutoDownload(input: { enabled: boolean }): Promise<MobileUpdateState>;
  onChange(listener: (state: MobileUpdateState) => void): Promise<() => void>;
}

export interface HomeShortcutStatus {
  supported: boolean;
  pinned: boolean;
  reason: string | null;
}

export interface HomeShortcutAdapter {
  status(input: { instanceId: string }): Promise<HomeShortcutStatus>;
  pin(input: {
    instanceId: string;
    name: string;
    iconImage?: string | null;
  }): Promise<{ pinned: boolean }>;
}

export interface MobileClient {
  authenticate<T>(path: string, input: unknown): Promise<T>;
  clearSession(): Promise<void>;
  openExternal(url: string): Promise<void>;
  openLink?(url: string): Promise<void>;
  openMap?(destination: import('shared').ComponentMapDestination): Promise<void>;
  downloadFile(input: FileDownloadInput): Promise<void>;
  updates?: MobileUpdateAdapter;
  homeShortcuts?: HomeShortcutAdapter;
  phoneControl?: {
    status(): Promise<MobileControlState>;
    settings(): Promise<void>;
    start(
      workspaceId: string,
      sessionId: string,
      scope?: 'session' | 'workspace',
    ): Promise<MobileControlState>;
    stop(): Promise<void>;
  };
  notifications?: {
    load(): Promise<MobileNotificationDeviceView>;
    update(input: MobileNotificationPreferences): Promise<MobileNotificationDeviceView>;
    requestPermission(): Promise<MobileNotificationDeviceView>;
  };
}

let client: MobileClient | null = null;

// 原生能力由 APP 入口注入，共享页面不依赖 Capacitor 或另存业务状态。
export function configureMobileClient(value: MobileClient | null): void {
  client = value;
}

export function getMobileClient(): MobileClient | null {
  return client;
}

export function mobileSession(result: unknown): MobileAuthResultView | null {
  if (!result || typeof result !== 'object' || !('accessToken' in result)) return null;
  if (
    typeof result.accessToken !== 'string' ||
    !result.accessToken ||
    !('refreshToken' in result) ||
    typeof result.refreshToken !== 'string' ||
    !result.refreshToken ||
    !('user' in result) ||
    !result.user ||
    typeof result.user !== 'object' ||
    !('id' in result.user) ||
    typeof result.user.id !== 'string' ||
    !result.user.id
  )
    throw new Error('移动端会话凭证不完整，请更新服务后重试');
  return result as MobileAuthResultView;
}

export async function openExternal(url: string, replace = false): Promise<void> {
  const target = new URL(url, window.location.href);
  if (!['http:', 'https:'].includes(target.protocol) || target.username || target.password)
    throw new Error('外部链接无效');
  if (client) await client.openExternal(target.href);
  else if (replace) window.location.assign(target.href);
  else window.open(target.href, '_blank', 'noopener,noreferrer');
}

export async function downloadFile(input: FileDownloadInput): Promise<void> {
  if (!input.url && !input.blob) throw new Error('文件内容不存在');
  const name =
    input.name.replace(/[\\/:*?"<>|\u0000-\u001f]/g, '_').replace(/^\.+$/, '下载文件') ||
    '下载文件';
  if (client) return client.downloadFile({ ...input, name });
  const url = input.url ?? URL.createObjectURL(input.blob!);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.append(link);
  try {
    link.click();
  } finally {
    link.remove();
    if (!input.url) setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}
